import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Page } from '../../layout/Page';
import { useOrgName, useTerms } from '../../layout';
import { Icon } from '../../design/Icon';
import { fmt } from '../../design/format';
import { Alert, Button, Drawer, EmptyState, ErrorState, Field, Input, MenuItem, Popover, Segmented, Switch, Tabs, usePopover, useToast } from '../../components/shared';
import { useAuth } from '../../contexts/AuthContext';
import adminAPI, { errorMessage } from '../../services/adminApi';
import { communityRows, lower, pluralOf, routedByText } from './adminModel';
import { CardSearch, useOrgFullName } from './adminShared';
import { CsvImport, IMPORT_STATE_KEY } from './CsvImport';
import { useAdminData } from './useAdminData';
import './admin.css';

const SUBTITLE = (terms) => `${terms.communities} calls are attributed to, the names callers use for them, and which queues and auto attendants route their calls.`;

const MAPPING_SOURCES = ['CallQueueMapping', 'AutoAttendantMapping'];
const DIRECTORY_SOURCES = ['DirectoryMatch', 'Manual'];
const WORDS_SOURCES = ['AnalysisExtraction'];

const sum = (mix, keys) => keys.reduce((n, k) => n + ((mix || {})[k] || 0), 0);

export function AdminCommunitiesPage() {
  const org = useOrgName();
  const orgFull = useOrgFullName();
  const terms = useTerms();
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const { data, loading, error, retry, reload } = useAdminData(
    {
      levels: adminAPI.getLevels,
      nodes: adminAPI.getNodes,
      communities: adminAPI.getCommunities,
      stats: () => adminAPI.getCommunityStats(30),
      entities: () => adminAPI.getRoutingEntities(30),
      attribution: adminAPI.getAttributionSettings,
    },
    { optional: ['attribution'] }
  );
  const initialTab = location.hash === '#import' ? 'import' : location.hash === '#mapping' ? 'map' : hasKeptImport() ? 'import' : 'list';
  const [tab, setTab] = useState(initialTab);
  const [chooser, setChooser] = useState(0);
  const [editing, setEditing] = useState(null);

  const showTab = (id) => {
    setTab(id);
    navigate({ hash: id === 'import' ? '#import' : id === 'map' ? '#mapping' : '' }, { replace: true });
  };

  const levels = data?.levels || [];
  const leafLevel = levels[levels.length - 1] || null;
  const levelName = leafLevel?.displayName || 'Group';
  const levelPlural = pluralOf(leafLevel);
  const communities = useMemo(() => {
    if (!data) return [];
    return communityRows(data.communities, data.nodes, data.stats);
  }, [data]);
  const activeCount = communities.filter((c) => c.isActive !== false).length;

  const exportColumns = [
    { key: 'name', header: terms.community },
    { key: 'code', header: 'Code' },
    { key: 'node', header: levelName, value: (c) => data?.nodes.find((n) => n.id === c.hierarchyNodeId)?.name || '' },
    { key: 'homeCount', header: 'Homes' },
    { key: 'aliases', header: 'Aliases', value: (c) => (c.aliases || []).join('; ') },
    { key: 'active', header: 'Active', value: (c) => (c.isActive === false ? 'No' : 'Yes') },
  ];

  const actions = (
    <>
      <Button onClick={() => setEditing({})} disabled={!data}>
        <Icon name="plus" className="ico-sm" />
        Add {terms.communityLower}
      </Button>
      <Button
        variant="primary"
        onClick={() => {
          showTab('import');
          setChooser((n) => n + 1);
        }}
      >
        <Icon name="upload" className="ico-sm" />
        Import CSV
      </Button>
    </>
  );

  const page = (children) => (
    <Page title={terms.communities} subtitle={SUBTITLE(terms)} actions={actions} exportConfig={{ name: terms.communities, columns: exportColumns, rows: communities }}>
      {children}
    </Page>
  );

  if (error) return page(<ErrorState title={`${terms.communities} couldn't load`} body={`Vertolytics couldn't read the ${terms.communityLower} directory. Nothing was changed.`} error={error} onRetry={retry} />);

  const tabs = (
    <Tabs
      label={terms.communities}
      value={tab}
      onChange={showTab}
      tabs={[
        { id: 'list', label: terms.communities, count: data ? activeCount : undefined },
        { id: 'map', label: 'Queue and auto attendant mapping', count: data ? data.entities.length : undefined },
        { id: 'import', label: 'Import from CSV' },
      ]}
    />
  );

  if (loading || !data) {
    return page(
      <>
        {tabs}
        <section className="card is-loading">
          <div className="card-body">
            <div style={{ height: 420 }} />
          </div>
        </section>
      </>
    );
  }

  const nodeById = new Map(data.nodes.map((n) => [n.id, n]));
  const statsById = new Map((data.stats || []).map((s) => [s.communityId, s]));
  const drawer = (
    <CommunityDrawer
      community={editing}
      nodes={data.nodes.filter((n) => n.levelId === leafLevel?.id)}
      levelName={levelName}
      onClose={() => setEditing(null)}
      onSaved={async (saved, isNew) => {
        setEditing(null);
        await reload();
        toast(isNew ? `Added ${saved.name}` : `Saved ${saved.name}`);
      }}
    />
  );

  const importer = (
    <CsvImport
      visible={tab === 'import'}
      levelName={levelName}
      levelPlural={levelPlural}
      orgName={org}
      openChooser={chooser}
      onPreview={() => showTab('import')}
      onApplied={async (entity) => {
        await reload();
        showTab(entity === 'mappings' ? 'map' : 'list');
      }}
    />
  );

  if (tab === 'list' && communities.length === 0) {
    return page(
      <>
        <EmptyState
          icon="home"
          title={`No ${terms.communitiesLower} yet`}
          body={`Add ${terms.communitiesLower} one at a time, or import ${org}'s directory from a CSV with name, ${leafLevel ? levelName : 'group'}, homes, and aliases.`}
          actions={
            <>
              <Button onClick={() => setEditing({})}>Add {terms.communityLower}</Button>
              <Button
                variant="primary"
                onClick={() => {
                  showTab('import');
                  setChooser((n) => n + 1);
                }}
              >
                Import CSV
              </Button>
            </>
          }
        />
        {importer}
        {drawer}
      </>
    );
  }

  return page(
    <>
      {tabs}
      {tab === 'list' && (
        <div className="stack">
          {data.attribution && <DirectoryConflictSetting settings={data.attribution} communities={communities} onSaved={reload} />}
          <CommunityList communities={communities} nodes={data.nodes} leafLevel={leafLevel} levelName={levelName} nodeById={nodeById} statsById={statsById} entities={data.entities} onEdit={setEditing} />
        </div>
      )}
      {tab === 'map' && <MappingTab data={data} levels={levels} levelName={levelName} orgFull={orgFull} onSaved={reload} />}
      {importer}
      {drawer}
    </>
  );
}

function hasKeptImport() {
  try {
    return Boolean(window.sessionStorage.getItem(IMPORT_STATE_KEY));
  } catch (e) {
    return false;
  }
}

function DirectoryConflictSetting({ settings, communities, onSaved }) {
  const terms = useTerms();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const [busy, setBusy] = useState(false);
  const canManage = hasPermission('directory:manage');
  const value = settings.directoryOverridesLine ? 'caller' : 'line';
  const [called, listed] = communities.filter((c) => c.isActive !== false).map((c) => c.name);
  const example =
    called && listed ? `For example, an owner listed under ${listed} calls the ${called} line.` : `For example, an owner listed under one ${terms.communityLower} calls another ${terms.communityLower}'s line.`;

  const change = async (next) => {
    if (next === value || busy || !canManage) return;
    setBusy(true);
    try {
      await adminAPI.saveAttributionSettings({ directoryOverridesLine: next === 'caller' });
      await onSaved();
      toast(next === 'caller' ? "The caller's directory listing now wins. Earlier calls are being updated." : 'The line now wins. Earlier calls are being updated.');
    } catch (err) {
      toast(errorMessage(err), 'alert');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="card-body row-wrap between" style={{ gap: 16 }}>
        <div style={{ maxWidth: 640 }}>
          <div className="strong">When a caller's directory {terms.communityLower} differs from the line they called</div>
          <div className="caption">
            {example} {terms.ACommunity} set by hand on a call always wins.
          </div>
        </div>
        <Segmented
          label="Directory and line conflicts"
          value={value}
          onChange={change}
          options={[
            { value: 'line', label: 'The line wins' },
            { value: 'caller', label: 'The caller wins' },
          ]}
        />
      </div>
    </section>
  );
}

function CommunityList({ communities, nodes, leafLevel, levelName, nodeById, statsById, entities, onEdit }) {
  const [query, setQuery] = useState('');
  const [nodeFilter, setNodeFilter] = useState(null);
  const pop = usePopover();
  const terms = useTerms();
  const filterNodes = nodes.filter((n) => n.levelId === leafLevel?.id).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name));
  const q = query.trim().toLowerCase();
  const visible = communities.filter(
    (c) => (!nodeFilter || c.hierarchyNodeId === nodeFilter) && (!q || c.name.toLowerCase().includes(q) || (c.aliases || []).some((a) => a.toLowerCase().includes(q)))
  );

  const routedBy = (community) => {
    const direct = routedByText(community, entities);
    if (direct) return <span>{direct}</span>;
    const viaNode = entities.some((e) => e.mapping && e.mapping.hierarchyNodeId && e.mapping.hierarchyNodeId === community.hierarchyNodeId);
    return <span className="muted">{viaNode ? `${levelName} queues` : 'Organization queues'}</span>;
  };

  return (
    <section className="card">
      <div className="card-head" style={{ alignItems: 'center' }}>
        <CardSearch value={query} onChange={setQuery} placeholder="Search names and aliases" />
        <div className="acts">
          <Popover
            open={pop.open}
            onClose={pop.close}
            align="right"
            width={220}
            label={`Filter by ${lower(levelName)}`}
            anchor={
              <button type="button" className={`chip${nodeFilter ? ' active' : ''}`} onClick={pop.toggle} aria-haspopup="true" aria-expanded={pop.open}>
                <span className="k">{levelName}</span>
                {nodeFilter ? nodeById.get(nodeFilter)?.name : 'All'}
                <Icon name="chevD" className="ico-sm" />
              </button>
            }
          >
            <MenuItem
              on={!nodeFilter}
              checkable
              onClick={() => {
                setNodeFilter(null);
                pop.close();
              }}
            >
              All {lower(pluralOf(leafLevel))}
            </MenuItem>
            {filterNodes.map((n) => (
              <MenuItem
                key={n.id}
                on={nodeFilter === n.id}
                checkable
                onClick={() => {
                  setNodeFilter(n.id);
                  pop.close();
                }}
              >
                {n.name}
              </MenuItem>
            ))}
          </Popover>
        </div>
      </div>
      <div className="card-body flush">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{terms.community}</th>
                <th>{levelName}</th>
                <th className="n">Homes</th>
                <th>Aliases callers use</th>
                <th>Routed by</th>
                <th className="n">Calls, 30 days</th>
                <th>How calls were attributed</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => {
                const stats = statsById.get(c.id);
                const calls = stats ? stats.callCount : 0;
                const mix = stats ? stats.attributionMix : {};
                const mapped = sum(mix, MAPPING_SOURCES);
                const directory = sum(mix, DIRECTORY_SOURCES);
                const words = sum(mix, WORDS_SOURCES);
                const total = mapped + directory + words;
                const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
                return (
                  <tr key={c.id}>
                    <td className="cell-title">
                      {c.name}
                      {c.isActive === false && (
                        <>
                          {' '}
                          <span className="badge">Inactive</span>
                        </>
                      )}
                    </td>
                    <td>{nodeById.get(c.hierarchyNodeId)?.name || <span className="caption">None</span>}</td>
                    <td className="n">{c.homeCount != null ? fmt.int(c.homeCount) : '—'}</td>
                    <td>
                      <div className="row-wrap" style={{ gap: 4 }}>
                        {(c.aliases || []).length ? (
                          c.aliases.map((a) => (
                            <span className="tag" key={a}>
                              {a}
                            </span>
                          ))
                        ) : (
                          <span className="caption">None</span>
                        )}
                      </div>
                    </td>
                    <td className="small">{routedBy(c)}</td>
                    <td className="n">{fmt.int(calls)}</td>
                    <td>
                      {total > 0 ? (
                        <div
                          style={{ display: 'flex', gap: 2, height: 8, width: 140 }}
                          title={`Queue mapping ${pct(mapped)}%, caller directory ${pct(directory)}%, caller's words ${pct(words)}%`}
                          role="img"
                          aria-label={`Queue mapping ${pct(mapped)}%, caller directory ${pct(directory)}%, caller's words ${pct(words)}%`}
                        >
                          {mapped > 0 && <span style={{ flex: mapped, background: 'var(--series-1)', borderRadius: 2 }} />}
                          {directory > 0 && <span style={{ flex: directory, background: 'var(--series-4)', borderRadius: 2 }} />}
                          {words > 0 && <span style={{ flex: words, background: 'var(--series-7)', borderRadius: 2 }} />}
                        </div>
                      ) : (
                        <span className="caption">No calls</span>
                      )}
                    </td>
                    <td>
                      <button className="btn btn-ghost btn-sm icon-btn" type="button" aria-label={`Edit ${c.name}`} title={`Edit ${c.name}`} onClick={() => onEdit(c)}>
                        <Icon name="edit" className="ico-sm" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {visible.length === 0 && (
            <p className="caption" style={{ padding: 16 }}>
              No {terms.communityLower} matches. Search also checks aliases.
            </p>
          )}
        </div>
      </div>
      <div className="table-foot">
        <div className="legend">
          <span>
            <i style={{ background: 'var(--series-1)' }} />
            Queue or auto attendant mapping
          </span>
          <span>
            <i style={{ background: 'var(--series-4)' }} />
            Caller directory match
          </span>
          <span>
            <i style={{ background: 'var(--series-7)' }} />
            Caller's words (language model, with quote)
          </span>
        </div>
      </div>
    </section>
  );
}

const TYPE_LABEL = { AutoAttendant: 'Auto attendant', CallQueue: 'Call queue' };

function mappingKind(mapping) {
  if (mapping?.communityId) return 'community';
  if (mapping?.hierarchyNodeId) return `node:${mapping.hierarchyNodeId}`;
  return 'org';
}

function MappingTab({ data, levels, levelName, orgFull, onSaved }) {
  const toast = useToast();
  const terms = useTerms();
  const [busy, setBusy] = useState(null);
  const [problem, setProblem] = useState(null);
  const [pending, setPending] = useState({});
  const nodesByLevel = useMemo(
    () => new Map(levels.map((l) => [l.id, data.nodes.filter((n) => n.levelId === l.id).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name))])),
    [levels, data.nodes]
  );
  const nodeLevel = new Map(data.nodes.map((n) => [n.id, n.levelId]));
  const communities = data.communities.filter((c) => c.isActive !== false).sort((a, b) => a.name.localeCompare(b.name));
  const entities = [...data.entities].sort(
    (a, b) => (a.routingEntityType === b.routingEntityType ? 0 : a.routingEntityType === 'AutoAttendant' ? -1 : 1) || a.routingEntityName.localeCompare(b.routingEntityName)
  );

  const keyOf = (entity) => `${entity.routingEntityType}:${entity.routingEntityId}`;
  const kindValueOf = (entity) => {
    const kind = mappingKind(entity.mapping);
    return kind.startsWith('node:') ? `level:${nodeLevel.get(entity.mapping.hierarchyNodeId)}` : kind;
  };
  const clearPending = (key) => setPending(({ [key]: _, ...rest }) => rest);

  const save = async (entity, change) => {
    const key = keyOf(entity);
    setBusy(key);
    setProblem(null);
    try {
      const base = entity.mapping || {
        routingEntityType: entity.routingEntityType,
        routingEntityId: entity.routingEntityId,
        routingEntityName: entity.routingEntityName,
        excludeFromAnalytics: false,
      };
      const next = { ...base, communityId: change.communityId || null, hierarchyNodeId: change.hierarchyNodeId || null };
      if (entity.mapping) await adminAPI.updateMapping(next);
      else await adminAPI.createMapping(next);
      await onSaved();
      clearPending(key);
      toast(`Saved mapping for ${entity.routingEntityName}`);
    } catch (err) {
      setProblem(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const onKind = (entity, value) => {
    const key = keyOf(entity);
    if (value === kindValueOf(entity)) return clearPending(key);
    if (value === 'org') return save(entity, {});
    return setPending((p) => ({ ...p, [key]: value }));
  };

  const pendingTarget = (entity, value) => {
    const isCommunity = value === 'community';
    const levelId = isCommunity ? null : value.slice('level:'.length);
    const level = levels.find((l) => l.id === levelId);
    const options = isCommunity ? communities : nodesByLevel.get(levelId) || [];
    const noun = isCommunity ? terms.communityLower : lower(level?.displayName || levelName);
    return (
      <select
        className="select"
        style={{ height: 30, minWidth: 180 }}
        value=""
        disabled={busy === keyOf(entity)}
        aria-label={`${isCommunity ? terms.community : level?.displayName || levelName} for ${entity.routingEntityName}`}
        onChange={(e) => e.target.value && save(entity, isCommunity ? { communityId: e.target.value } : { hierarchyNodeId: e.target.value })}
      >
        <option value="" disabled>
          {`Choose ${noun}…`}
        </option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
    );
  };

  return (
    <div className="stack">
      <div className="alert">
        <Icon name="info" />
        <div>
          The most specific mapping wins: {terms.aCommunity} mapping attributes every call. A {levelName} or organization mapping narrows the choices, then Vertolytics identifies the {terms.communityLower} from the
          caller's words or the caller directory and keeps the quote as evidence. Unmatched calls show as Unknown {terms.communityLower}.
        </div>
      </div>
      {problem && <Alert tone="error">{problem}</Alert>}
      <section className="card">
        <div className="card-body flush">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Name in Teams</th>
                  <th>Maps to</th>
                  <th>
                    {levelName} or {terms.communityLower}
                  </th>
                  <th className="n">Calls, 30 days</th>
                  <th>Unknown {terms.communityLower}</th>
                </tr>
              </thead>
              <tbody>
                {entities.map((entity) => {
                  const key = keyOf(entity);
                  const kind = mappingKind(entity.mapping);
                  const kindValue = pending[key] || kindValueOf(entity);
                  const unknownPct = entity.callCount ? Math.round(((entity.unknownCommunityCallCount || 0) / entity.callCount) * 100) : null;
                  return (
                    <tr key={key}>
                      <td className="small muted nowrap">{TYPE_LABEL[entity.routingEntityType] || entity.routingEntityType}</td>
                      <td className="cell-title">{entity.routingEntityName}</td>
                      <td>
                        <select
                          className="select"
                          style={{ height: 30, minWidth: 140 }}
                          value={kindValue}
                          disabled={busy === key}
                          aria-label={`What ${entity.routingEntityName} maps to`}
                          onChange={(e) => onKind(entity, e.target.value)}
                        >
                          <option value="org">Whole organization</option>
                          {levels.map((l) => (
                            <option key={l.id} value={`level:${l.id}`}>
                              A {lower(l.displayName)}
                            </option>
                          ))}
                          <option value="community">{terms.ACommunity}</option>
                        </select>
                      </td>
                      <td>
                        {pending[key] ? (
                          pendingTarget(entity, pending[key])
                        ) : kind === 'org' ? (
                          <select className="select" style={{ height: 30, minWidth: 180 }} value="" aria-label={`Target of ${entity.routingEntityName}`} onChange={() => {}}>
                            <option value="">{levels.length ? `All ${lower(pluralOf(levels[0]))}` : orgFull}</option>
                          </select>
                        ) : kind === 'community' ? (
                          <select
                            className="select"
                            style={{ height: 30, minWidth: 180 }}
                            value={entity.mapping.communityId}
                            disabled={busy === key}
                            aria-label={`${terms.community} for ${entity.routingEntityName}`}
                            onChange={(e) => save(entity, { communityId: e.target.value })}
                          >
                            {communities.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <select
                            className="select"
                            style={{ height: 30, minWidth: 180 }}
                            value={entity.mapping.hierarchyNodeId}
                            disabled={busy === key}
                            aria-label={`${levelName} for ${entity.routingEntityName}`}
                            onChange={(e) => save(entity, { hierarchyNodeId: e.target.value })}
                          >
                            {(nodesByLevel.get(nodeLevel.get(entity.mapping.hierarchyNodeId)) || []).map((n) => (
                              <option key={n.id} value={n.id}>
                                {n.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className="n">{fmt.int(entity.callCount || 0)}</td>
                      <td>
                        {kind === 'community' ? (
                          <span className="caption">Not applicable</span>
                        ) : unknownPct == null ? (
                          <span className="caption">No calls</span>
                        ) : (
                          <>
                            <span className="small num">{unknownPct}%</span>
                            {unknownPct > 11 && (
                              <>
                                {' '}
                                <span className="badge b-warning">
                                  <Icon name="alert" />
                                  Add aliases
                                </span>
                              </>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {entities.length === 0 && (
              <p className="caption" style={{ padding: 16 }}>
                No auto attendants or call queues yet. They appear here once call records sync, or import a mapping file.
              </p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function CommunityDrawer({ community, nodes, levelName, onClose, onSaved }) {
  const isNew = community && !community.id;
  const terms = useTerms();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  useEffect(() => {
    if (!community) return;
    setProblem(null);
    setForm({
      name: community.name || '',
      code: community.code || '',
      hierarchyNodeId: community.hierarchyNodeId || '',
      homeCount: community.homeCount != null ? String(community.homeCount) : '',
      aliases: (community.aliases || []).join(', '),
      isActive: community.isActive !== false,
    });
  }, [community]);

  if (!community || !form) return null;
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const homes = form.homeCount.trim() === '' ? null : Number(form.homeCount);
  const homesInvalid = homes != null && (!Number.isInteger(homes) || homes < 0 || homes > 100000);

  const submit = async () => {
    setBusy(true);
    setProblem(null);
    const body = {
      ...community,
      name: form.name.trim(),
      code: form.code.trim() || null,
      hierarchyNodeId: form.hierarchyNodeId || null,
      homeCount: homes,
      aliases: form.aliases
        .split(/[,;]/)
        .map((a) => a.trim())
        .filter(Boolean),
      isActive: form.isActive,
    };
    try {
      const saved = isNew ? await adminAPI.createCommunity(body) : await adminAPI.updateCommunity(body);
      await onSaved(saved, isNew);
    } catch (err) {
      setProblem(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={isNew ? `Add ${terms.communityLower}` : `Edit ${community.name}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={busy || !form.name.trim() || homesInvalid}>
            {busy ? 'Saving…' : isNew ? `Add ${terms.communityLower}` : `Save ${terms.communityLower}`}
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Name" htmlFor="cm-name" hint="The full name, as it appears on the association's documents.">
          <Input id="cm-name" value={form.name} onChange={set('name')} autoFocus />
        </Field>
        <Field label={levelName} htmlFor="cm-node">
          <select className="select" id="cm-node" value={form.hierarchyNodeId} onChange={set('hierarchyNodeId')}>
            <option value="">None</option>
            {nodes.map((n) => (
              <option key={n.id} value={n.id}>
                {n.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="form-grid">
          <Field label="Homes" htmlFor="cm-homes" error={homesInvalid ? 'Enter a whole number from 0 to 100,000.' : null}>
            <Input id="cm-homes" inputMode="numeric" value={form.homeCount} invalid={homesInvalid} onChange={set('homeCount')} />
          </Field>
          <Field label="Code" htmlFor="cm-code" hint="Optional. Matches CSV rows when names change.">
            <Input id="cm-code" value={form.code} onChange={set('code')} />
          </Field>
        </div>
        <Field label="Aliases callers use" htmlFor="cm-aliases" hint={`Separate with commas. Used to recognize the ${terms.communityLower} in calls and CSV files.`}>
          <Input id="cm-aliases" value={form.aliases} onChange={set('aliases')} />
        </Field>
        <Switch label="Active" checked={form.isActive} onChange={set('isActive')} />
        {problem && <Alert tone="error">{problem}</Alert>}
      </div>
    </Drawer>
  );
}
